/** Workflow role codes returned by GET /OnBoarding/userroles and login/settings. */
export const WorkflowRoleCode = {
  CUSTOMER: "customer",
  IMPLEMENTATION_TEAM: "implementationTeam",
  TPM: "tpm",
  ADMIN: "admin",
} as const;

export type WorkflowRoleCodeValue =
  (typeof WorkflowRoleCode)[keyof typeof WorkflowRoleCode];

/** Onboarding workflow status values from GET …/GetSettings.status. */
export const WorkflowStatus = {
  INITIAL: "initial",
  DRAFT: "draft",
  CUSTOMER_SUBMITTED: "customerSubmitted",
  IMPLEMENTATION_SUBMITTED: "implementationSubmitted",
  SUBMITTED: "submitted",
} as const;

export function normalizeRoleCode(code: string | undefined | null): string {
  return String(code ?? "")
    .trim()
    .toLowerCase();
}

export function normalizeWorkflowStatus(
  status: string | undefined | null,
): string {
  return String(status ?? WorkflowStatus.DRAFT)
    .trim()
    .toLowerCase();
}

/** Role that may submit at the current workflow stage; null when workflow is complete. */
export function getSubmitRoleForStatus(
  status: string | undefined | null,
): string | null {
  const s = normalizeWorkflowStatus(status);
  switch (s) {
    case WorkflowStatus.INITIAL.toLowerCase():
    case WorkflowStatus.DRAFT.toLowerCase():
      return WorkflowRoleCode.CUSTOMER;
    case WorkflowStatus.CUSTOMER_SUBMITTED.toLowerCase():
      return WorkflowRoleCode.IMPLEMENTATION_TEAM;
    case WorkflowStatus.IMPLEMENTATION_SUBMITTED.toLowerCase():
      return WorkflowRoleCode.TPM;
    case WorkflowStatus.SUBMITTED.toLowerCase():
      return null;
    default:
      return WorkflowRoleCode.CUSTOMER;
  }
}

const sameWorkflowRole = (left: unknown, right: string): boolean =>
  normalizeRoleCode(legacyRoleToCode(left)) === normalizeRoleCode(right);

export function inferWorkflowReadOnly(
  userRoleCode: string | undefined | null,
  status: string | undefined | null,
): boolean {
  const normalizedStatus = normalizeWorkflowStatus(status);
  if (normalizedStatus === WorkflowStatus.SUBMITTED.toLowerCase()) {
    return true;
  }
  const requiredSubmitRole = getSubmitRoleForStatus(status);
  if (!requiredSubmitRole) return true;
  return !sameWorkflowRole(userRoleCode, requiredSubmitRole);
}

export function canRoleSubmit(
  userRoleCode: string | undefined | null,
  status: string | undefined | null,
): boolean {
  const required = getSubmitRoleForStatus(status);
  if (!required) return false;
  return sameWorkflowRole(userRoleCode, required);
}

export function isCustomerRole(code: string | undefined | null): boolean {
  return normalizeRoleCode(code) === WorkflowRoleCode.CUSTOMER;
}

export function isInternalWorkflowRole(
  code: string | undefined | null,
): boolean {
  const normalized = normalizeRoleCode(code);
  return (
    normalized !== WorkflowRoleCode.CUSTOMER &&
    normalized !== "" &&
    normalized !== "unknown"
  );
}

/** Tooltip Admin access: implementationTeam, tpm, or admin. */
export function canEditTooltips(
  code: string | number | undefined | null,
): boolean {
  // Normalize legacy numeric roles (1/2) and aliases ("agilysys") to workflow codes.
  const coerced =
    typeof code === "number"
      ? code
      : typeof code === "string" && /^\d+$/.test(code.trim())
        ? Number(code.trim())
        : code;
  // normalizeRoleCode lowercases; compare against lowercased constants too
  // ("implementationteam" !== "implementationTeam" otherwise).
  const normalized = normalizeRoleCode(legacyRoleToCode(coerced));
  return (
    normalized === normalizeRoleCode(WorkflowRoleCode.IMPLEMENTATION_TEAM) ||
    normalized === normalizeRoleCode(WorkflowRoleCode.TPM) ||
    normalized === normalizeRoleCode(WorkflowRoleCode.ADMIN)
  );
}

export function getWorkflowStatusLabel(
  status: string | undefined | null,
): string {
  const s = normalizeWorkflowStatus(status);
  switch (s) {
    case WorkflowStatus.INITIAL.toLowerCase():
      return "Initial — awaiting customer submission";
    case WorkflowStatus.DRAFT.toLowerCase():
      return "Draft — awaiting customer submission";
    case WorkflowStatus.CUSTOMER_SUBMITTED.toLowerCase():
      return "Customer submitted — implementation team review";
    case WorkflowStatus.IMPLEMENTATION_SUBMITTED.toLowerCase():
      return "Implementation submitted — TPM final review";
    case WorkflowStatus.SUBMITTED.toLowerCase():
      return "Submitted — onboarding complete";
    default:
      return `Status: ${status ?? "unknown"}`;
  }
}

/** Maps legacy settings `role` strings/ids to workflow role codes.
 * Catalog ids: 1 customer, 2 implementationTeam, 3 tpm, 4 agilysys, 5 admin.
 */
export function legacyRoleToCode(role: unknown): string {
  if (typeof role === "string") {
    const r = role.trim().toLowerCase();
    if (r === "customer") return WorkflowRoleCode.CUSTOMER;
    if (r === "agilysys" || r === "implementationteam") {
      return WorkflowRoleCode.IMPLEMENTATION_TEAM;
    }
    if (r === "tpm") return WorkflowRoleCode.TPM;
    if (r === "admin") return WorkflowRoleCode.ADMIN;
    // Numeric id sent as string ("2")
    if (/^\d+$/.test(r)) {
      return legacyRoleToCode(Number(r));
    }
    return r;
  }
  if (typeof role === "number") {
    if (role === 1) return WorkflowRoleCode.CUSTOMER;
    if (role === 2) return WorkflowRoleCode.IMPLEMENTATION_TEAM;
    if (role === 3) return WorkflowRoleCode.TPM;
    if (role === 4) return WorkflowRoleCode.IMPLEMENTATION_TEAM; // agilysys
    if (role === 5) return WorkflowRoleCode.ADMIN;
    return WorkflowRoleCode.CUSTOMER;
  }
  return WorkflowRoleCode.CUSTOMER;
}
